import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useScrollToSection } from '../../hooks/useScrollToSection';
import { SOCIAL_LINKS } from '../../data/constants';
import { IconGithub, IconLinkedin, IconMail, IconArrowRight } from '../ui/Icons';
import './Hero.css';

const ROLE_TAGS = [
  'Solutions Architect',
  'GenAI · RAG · LLM',
  'Lakehouse · BigQuery · Snowflake',
  'AWS · GCP · Kubernetes',
];

const socialIconMap: Record<string, React.ReactNode> = {
  github:   <IconGithub   size={18} />,
  linkedin: <IconLinkedin size={18} />,
  email:    <IconMail     size={18} />,
};

/* ─────────────────────────────────────────────
   Tech constellation data
───────────────────────────────────────────── */

/** One tech pill — cx/cy are SVG coords in the 500×460 viewBox */
const PILLS = [
  // AI / GenAI cluster — top band
  { label: 'LangChain',  cx: 148, cy: 42,  w: 66,  d: 0.20 },
  { label: 'RAG',        cx: 240, cy: 26,  w: 36,  d: 0.28 },
  { label: 'LLM',        cx: 310, cy: 46,  w: 36,  d: 0.24 },
  { label: 'Pinecone',   cx: 388, cy: 30,  w: 62,  d: 0.32 },
  { label: 'FastAPI',    cx: 456, cy: 66,  w: 56,  d: 0.38 },

  // Data cluster — left column
  { label: 'BigQuery',   cx: 58,  cy: 158, w: 64,  d: 0.40 },
  { label: 'Snowflake',  cx: 140, cy: 196, w: 68,  d: 0.45 },
  { label: 'dbt',        cx: 55,  cy: 230, w: 34,  d: 0.50 },
  { label: 'Airflow',    cx: 118, cy: 270, w: 54,  d: 0.55 },
  { label: 'Spark',      cx: 56,  cy: 308, w: 44,  d: 0.60 },

  // Cloud / Infra — right column
  { label: 'Kubernetes', cx: 416, cy: 126, w: 74,  d: 0.40 },
  { label: 'AWS',        cx: 455, cy: 165, w: 36,  d: 0.45 },
  { label: 'GCP',        cx: 448, cy: 202, w: 36,  d: 0.50 },
  { label: 'Terraform',  cx: 420, cy: 248, w: 68,  d: 0.55 },

  // Backend — bottom band
  { label: 'Golang',     cx: 170, cy: 402, w: 52,  d: 0.60 },
  { label: 'Python',     cx: 258, cy: 416, w: 54,  d: 0.65 },
  { label: 'GraphQL',    cx: 345, cy: 402, w: 60,  d: 0.70 },
  { label: 'PostgreSQL', cx: 428, cy: 360, w: 72,  d: 0.75 },
];

/** Impact metrics — sit in the open spaces between clusters */
const STATS = [
  { value: '98%',  sub: 'AI Accuracy',  cx: 246, cy: 138, d: 0.85 },
  { value: '40%',  sub: 'Latency Cut',  cx: 148, cy: 335, d: 0.95 },
  { value: '1M+',  sub: 'Users Served', cx: 360, cy: 295, d: 1.05 },
];

/** Lines: [x1,y1, x2,y2, bright?]
 *  bright=true → metric-to-tech connections */
const LINES: [number,number,number,number,boolean][] = [
  // within AI cluster
  [148,42,  240,26,  false],
  [240,26,  310,46,  false],
  [310,46,  388,30,  false],
  [388,30,  456,66,  false],
  // within Data cluster
  [58,158,  140,196, false],
  [55,230,  118,270, false],
  [118,270, 56,308,  false],
  // within Cloud cluster
  [416,126, 455,165, false],
  [455,165, 448,202, false],
  [448,202, 420,248, false],
  // within Backend cluster
  [170,402, 258,416, false],
  [258,416, 345,402, false],
  [345,402, 428,360, false],
  // 98% ← AI techs
  [246,138, 148,42,  true ],
  [246,138, 310,46,  true ],
  [246,138, 240,26,  true ],
  // 40% ← Data techs
  [148,335, 56,308,  true ],
  [148,335, 118,270, true ],
  // 1M+ ← Cloud + Backend
  [360,295, 420,248, true ],
  [360,295, 428,360, true ],
  [360,295, 345,402, true ],
];

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

const TechPill: React.FC<{ p: typeof PILLS[0] }> = ({ p }) => {
  const h = 24;
  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: [0, -5, 0, 5, 0],
      }}
      transition={{
        opacity: { duration: 0.45, delay: p.d },
        scale:   { duration: 0.45, delay: p.d },
        y: {
          duration: 4 + p.d * 0.8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: p.d + 0.5,
        },
      }}
    >
      <rect
        x={p.cx - p.w / 2} y={p.cy - h / 2}
        width={p.w} height={h} rx="12"
        fill="rgba(12,12,22,0.92)"
        stroke="rgba(99,102,241,0.28)"
        strokeWidth="1"
      />
      <text
        x={p.cx} y={p.cy + 1}
        textAnchor="middle" dominantBaseline="middle"
        fill="rgba(200,202,220,0.82)"
        fontSize="8.5"
        fontFamily="'SF Mono','Fira Code',monospace"
        fontWeight="500"
        letterSpacing="0.04em"
      >
        {p.label}
      </text>
    </motion.g>
  );
};

const MetricCard: React.FC<{ s: typeof STATS[0] }> = ({ s }) => (
  <motion.g
    initial={{ opacity: 0, scale: 0.6 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6, delay: s.d, ease: [0.22, 1, 0.36, 1] }}
  >
    {/* Outer glow */}
    <rect
      x={s.cx - 46} y={s.cy - 30}
      width="92" height="60" rx="14"
      fill="none"
      stroke="rgba(99,102,241,0.12)"
      strokeWidth="6"
      style={{ filter: 'blur(4px)' }}
    />
    {/* Card */}
    <rect
      x={s.cx - 46} y={s.cy - 30}
      width="92" height="60" rx="14"
      fill="rgba(10,10,20,0.96)"
      stroke="rgba(99,102,241,0.42)"
      strokeWidth="1.5"
    />
    {/* Value */}
    <text
      x={s.cx} y={s.cy - 3}
      textAnchor="middle"
      fill="#818CF8"
      fontSize="24"
      fontFamily="Inter,-apple-system,sans-serif"
      fontWeight="900"
      letterSpacing="-0.04em"
    >
      {s.value}
    </text>
    {/* Sub-label */}
    <text
      x={s.cx} y={s.cy + 16}
      textAnchor="middle"
      fill="rgba(113,113,130,0.9)"
      fontSize="7"
      fontFamily="'SF Mono','Fira Code',monospace"
      letterSpacing="0.1em"
    >
      {s.sub.toUpperCase()}
    </text>
  </motion.g>
);

/* ─────────────────────────────────────────────
   Main visual
───────────────────────────────────────────── */
const ConstellationVisual: React.FC = () => (
  <div className="hero__arch-wrap">
    <svg viewBox="0 0 500 460" className="hero__arch-svg" aria-hidden="true">
      <defs>
        <radialGradient id="cBg1" cx="50%" cy="40%" r="55%">
          <stop offset="0%"   stopColor="rgba(99,102,241,0.07)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="cBg2" cx="20%" cy="65%" r="35%">
          <stop offset="0%"   stopColor="rgba(139,92,246,0.05)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="cBg3" cx="80%" cy="65%" r="35%">
          <stop offset="0%"   stopColor="rgba(99,102,241,0.05)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>

      {/* Ambient background glows */}
      <rect width="500" height="460" fill="url(#cBg1)" />
      <rect width="500" height="460" fill="url(#cBg2)" />
      <rect width="500" height="460" fill="url(#cBg3)" />

      {/* Connection lines — render first (behind everything) */}
      {LINES.map(([x1,y1,x2,y2,bright], i) => (
        <motion.line
          key={i}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={bright ? 'rgba(99,102,241,0.35)' : 'rgba(99,102,241,0.14)'}
          strokeWidth={bright ? 1 : 0.75}
          strokeDasharray={bright ? '0' : '3 4'}
          initial={{ opacity: 0, pathLength: 0 }}
          animate={{ opacity: 1, pathLength: 1 }}
          transition={{ duration: 0.8, delay: 0.6 + i * 0.03 }}
        />
      ))}

      {/* Tech pills */}
      {PILLS.map(p => <TechPill key={p.label} p={p} />)}

      {/* Metric cards — rendered on top */}
      {STATS.map(s => <MetricCard key={s.value} s={s} />)}
    </svg>
  </div>
);

/* ─────────────────────────────────────────────
   Hero section
───────────────────────────────────────────── */
export const Hero: React.FC = () => {
  const heroRef    = useRef<HTMLElement>(null);
  const scrollTo   = useScrollToSection();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const yParallax  = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacityOut = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  const cVars = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
  };
  const iVars: any = {
    hidden:  { opacity: 0, y: 28, filter: 'blur(8px)' },
    visible: { opacity: 1, y: 0,  filter: 'blur(0px)', transition: { duration: 0.7, ease: [0.22,1,0.36,1] } },
  };

  return (
    <section id="home" className="hero" ref={heroRef}>
      <div className="hero__grid-bg" aria-hidden="true" />

      <div className="container">
        <motion.div
          className="hero__layout"
          variants={cVars}
          initial="hidden"
          animate="visible"
          style={{ opacity: opacityOut }}
        >
          {/* ── Left: text ── */}
          <div className="hero__content">
            <motion.div className="hero__tags" variants={iVars}>
              {ROLE_TAGS.map(t => <span key={t} className="hero__tag">{t}</span>)}
            </motion.div>

            <motion.h1 className="hero__name" variants={iVars}>
              Supratick<br />
              <span className="hero__name-accent">Dey</span>
            </motion.h1>

            <motion.p className="hero__bio" variants={iVars}>
              I design and ship production-grade AI and data systems. RAG pipelines,
              LLM fine-tuning, and petabyte-scale Lakehouse architectures for healthcare,
              pharma and fintech clients across Asia.
            </motion.p>

            <motion.div className="hero__actions" variants={iVars}>
              <motion.button
                className="btn btn--primary"
                onClick={() => scrollTo('contact')}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 18 }}
              >
                Get in Touch <IconArrowRight size={16} />
              </motion.button>
              <motion.a
                className="btn btn--ghost"
                href="https://linkedin.com/in/supratick-dey"
                target="_blank" rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 18 }}
              >
                View LinkedIn
              </motion.a>
            </motion.div>

            <motion.div className="hero__socials" variants={iVars}>
              {SOCIAL_LINKS.filter(l => socialIconMap[l.icon]).map((link, i) => (
                <motion.a
                  key={link.platform}
                  href={link.url}
                  target="_blank" rel="noopener noreferrer"
                  className="hero__social"
                  aria-label={link.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 + i * 0.1 }}
                  whileHover={{ y: -3, transition: { type: 'spring', stiffness: 400 } }}
                >
                  {socialIconMap[link.icon]}
                </motion.a>
              ))}
            </motion.div>
          </div>

          {/* ── Right: constellation ── */}
          <motion.div className="hero__visual" variants={iVars} style={{ y: yParallax }}>
            <ConstellationVisual />
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        className="hero__scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
      >
        <motion.div
          className="hero__scroll-line"
          animate={{ scaleY: [0.4, 1, 0.4] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </section>
  );
};
